import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  useTheme
} from '@mui/material';
import { weatherService } from '../../services/weatherService';
import Weather from '../../components/farm/weather/Weather';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import OpacityIcon from '@mui/icons-material/Opacity';
import AirIcon from '@mui/icons-material/Air';
import GrainIcon from '@mui/icons-material/Grain';
import BugReportIcon from '@mui/icons-material/BugReport';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface ForecastDay {
  dt: number;
  main: {
    temp: number;
    temp_min: number;
    temp_max: number;
    humidity: number;
    feels_like: number;
  };
  weather: [{
    main: string;
    description: string;
    icon: string;
  }];
  wind: {
    speed: number;
  };
  dt_txt: string;
}

interface CropRecommendation {
  name: string;
  suitability: 'high' | 'medium' | 'low';
  notes: string;
}

interface PestAlert {
  name: string;
  risk: 'high' | 'medium' | 'low';
  crops: string[];
  prevention: string;
}

const getSuitabilityColor = (suitability: string) => {
  switch (suitability) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'error';
    default:
      return 'default';
  }
};

const WeatherPage: React.FC = () => {
  const theme = useTheme();
  const [location, setLocation] = useState('');
  const [searchedLocation, setSearchedLocation] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [forecast, setForecast] = useState<ForecastDay[]>([]);
  const [forecastLoading, setForecastLoading] = useState<boolean>(false);
  const [forecastError, setForecastError] = useState<string | null>(null);
  const [cropRecommendations, setCropRecommendations] = useState<CropRecommendation[]>([]);
  const [pestAlerts, setPestAlerts] = useState<PestAlert[]>([]);
  const [insightsLoading, setInsightsLoading] = useState<boolean>(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    // Fetch forecast data when user navigates to the forecast tab
    if (newValue === 1 && forecast.length === 0) {
      fetchForecast();
    }
    
    // Fetch agricultural insights when user navigates to the insights tab
    if (newValue === 2 && cropRecommendations.length === 0) {
      fetchAgriculturalInsights();
    }
  };

  const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocation(event.target.value);
  };

  const handleSearch = () => {
    if (location.trim()) {
      setSearchedLocation(location);
      
      // If on forecast tab, fetch forecast for the new location
      if (activeTab === 1) {
        fetchForecast();
      }
      
      // If on insights tab, update insights for the new location
      if (activeTab === 2) {
        fetchAgriculturalInsights();
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Fetch 5-day forecast data
  const fetchForecast = async () => {
    if (!location.trim() && !searchedLocation) return;
    
    try {
      setForecastLoading(true);
      setForecastError(null);
      
      // Try to get coordinates for the location
      let lat = -1.2921; // Default to Nairobi
      let lon = 36.8219;
      
      try {
        const geoResponse = await fetch(
          `https://api.openweathermap.org/geo/1.0/direct?q=${searchedLocation || location}&limit=1&appid=${import.meta.env.VITE_WEATHER_API_KEY}`
        );
        const geoData = await geoResponse.json();
        
        if (geoData && geoData.length > 0) {
          lat = geoData[0].lat;
          lon = geoData[0].lon;
        }
      } catch (error) {
        console.error('Error getting coordinates:', error);
        // Continue with default coordinates
      }
      
      const forecastData = await weatherService.getForecast(lat, lon);
      
      // Process data to get one forecast per day (noon)
      const processedData = forecastData.list || [];
      
      // Group forecasts by day and take the noon forecast for each day
      const dailyForecasts: {[key: string]: ForecastDay} = {};
      processedData.forEach((item: ForecastDay) => {
        const date = new Date(item.dt * 1000);
        const day = date.toISOString().split('T')[0];
        const hour = date.getHours();
        
        // If we haven't saved a forecast for this day yet, or if this forecast is closer to noon
        if (!dailyForecasts[day] || Math.abs(hour - 12) < Math.abs(new Date(dailyForecasts[day].dt * 1000).getHours() - 12)) {
          dailyForecasts[day] = item;
        }
      });
      
      setForecast(Object.values(dailyForecasts).slice(0, 5));
      setForecastLoading(false);
    } catch (error) {
      console.error('Error fetching forecast:', error);
      setForecastError('Failed to load forecast data. Please try again later.');
      setForecastLoading(false);
    }
  };

  // Fetch agricultural insights based on weather
  const fetchAgriculturalInsights = async () => {
    setInsightsLoading(true);
    
    // In a real application, this would come from an API based on location and weather
    setTimeout(() => {
      // Mock crop recommendations based on Kenya's climate
      setCropRecommendations([
        {
          name: 'Maize',
          suitability: 'high',
          notes: 'Optimal growing conditions with current rainfall and temperature. Plant in well-drained soil.'
        },
        {
          name: 'Beans',
          suitability: 'high',
          notes: 'Favorable conditions for planting. Good companion plant with maize.'
        },
        {
          name: 'Tomatoes',
          suitability: 'medium',
          notes: 'Watch for humidity-related diseases. Consider greenhouse cultivation for best results.'
        },
        {
          name: 'Coffee',
          suitability: 'high',
          notes: 'Excellent conditions in higher elevation areas. Mind proper shade and drainage.'
        },
        {
          name: 'Sweet Potatoes',
          suitability: 'high',
          notes: 'Resilient in current conditions. Good choice for food security.'
        }
      ]);
      
      // Mock pest alerts based on current weather
      setPestAlerts([
        {
          name: 'Fall Armyworm',
          risk: 'high',
          crops: ['Maize', 'Sorghum'],
          prevention: 'Monitor fields regularly. Apply recommended pesticides at first sign of infestation.'
        },
        {
          name: 'Tomato Blight',
          risk: 'medium',
          crops: ['Tomatoes', 'Potatoes'],
          prevention: 'Ensure good air circulation between plants. Apply fungicide preventatively.'
        },
        {
          name: 'Aphids',
          risk: 'low',
          crops: ['Vegetables', 'Fruits'],
          prevention: 'Introduce natural predators like ladybugs. Spray with neem oil solution.'
        }
      ]);
      
      setInsightsLoading(false);
    }, 1000);
  };

  // Format date from UNIX timestamp or date string
  const formatDate = (dateString: string | number) => {
    const date = typeof dateString === 'number' 
      ? new Date(dateString * 1000) 
      : new Date(dateString);
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Weather & Agricultural Insights
        </Typography>
        
        <Paper elevation={1} sx={{ mb: 4 }}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Search Location"
                  placeholder="Enter city name (e.g., Nairobi, Mombasa, Kisumu)"
                  value={location}
                  onChange={handleLocationChange}
                  onKeyPress={handleKeyPress}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <LocationOnIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  onClick={handleSearch}
                  disabled={!location}
                  startIcon={<SearchIcon />}
                >
                  Get Weather
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Weather Dashboard" />
            <Tab label="5-Day Forecast" />
            <Tab label="Agricultural Insights" />
          </Tabs>
          <Divider />

          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Weather cityName={searchedLocation} />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              {!searchedLocation && forecast.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Enter a location and click "Get Weather" to see the forecast
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The 5-day forecast will show detailed weather information
                  </Typography>
                </Box>
              ) : forecastLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : forecastError ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="error">{forecastError}</Typography>
                </Paper>
              ) : (
                <>
                  <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                    5-Day Forecast for {searchedLocation || location}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {forecast.map((day) => (
                      <Grid item xs={12} sm={6} md={2.4} key={day.dt}>
                        <Card elevation={2} sx={{ 
                          height: '100%',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[4],
                          }
                        }}>
                          <CardContent>
                            <Typography variant="h6" align="center" gutterBottom>
                              {formatDate(day.dt)}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                              <img 
                                src={`https://openweathermap.org/img/w/${day.weather[0].icon}.png`}
                                alt={day.weather[0].description}
                                style={{ width: 64, height: 64 }}
                              />
                            </Box>
                            
                            <Typography variant="body1" align="center" gutterBottom>
                              {day.weather[0].main}
                            </Typography>
                            
                            <Typography variant="h5" align="center" gutterBottom>
                              {Math.round(day.main.temp)}°C
                            </Typography>
                            
                            <Divider sx={{ my: 1 }} />
                            
                            <Grid container spacing={1} sx={{ mt: 1 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" display="block">
                                  Min: {Math.round(day.main.temp_min)}°C
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" display="block">
                                  Max: {Math.round(day.main.temp_max)}°C
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" display="block">
                                  <OpacityIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                  {day.main.humidity}%
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" display="block">
                                  <AirIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                  {day.wind.speed} m/s
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ p: 3 }}>
              {!searchedLocation && cropRecommendations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Enter a location and click "Get Weather" to see agricultural insights
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Agricultural insights are based on weather conditions and patterns
                  </Typography>
                </Box>
              ) : insightsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                    Agricultural Insights for {searchedLocation || location || 'Kenya'}
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {/* Crop Recommendations */}
                    <Grid item xs={12} md={6}>
                      <Card elevation={3}>
                        <CardContent>
                          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GrainIcon /> Crop Recommendations
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          
                          <List>
                            {cropRecommendations.map((crop, index) => (
                              <React.Fragment key={crop.name}>
                                <ListItem alignItems="flex-start">
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="subtitle1">{crop.name}</Typography>
                                        <Chip 
                                          label={`${crop.suitability.charAt(0).toUpperCase() + crop.suitability.slice(1)} Suitability`}
                                          size="small"
                                          color={getSuitabilityColor(crop.suitability) as any}
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {crop.notes}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                                {index < cropRecommendations.length - 1 && <Divider />}
                              </React.Fragment>
                            ))}
                          </List>
                          
                          <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                            <CalendarTodayIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            Recommendations based on current and forecasted weather conditions
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Pest & Disease Alerts */}
                    <Grid item xs={12} md={6}>
                      <Card elevation={3}>
                        <CardContent>
                          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BugReportIcon /> Pest & Disease Alerts
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          
                          <List>
                            {pestAlerts.map((pest, index) => (
                              <React.Fragment key={pest.name}>
                                <ListItem alignItems="flex-start">
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="subtitle1">{pest.name}</Typography>
                                        <Chip 
                                          label={`${pest.risk.charAt(0).toUpperCase() + pest.risk.slice(1)} Risk`}
                                          size="small"
                                          color={getSuitabilityColor(pest.risk) as any}
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                          <strong>Affected Crops:</strong> {pest.crops.join(', ')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          <strong>Prevention:</strong> {pest.prevention}
                                        </Typography>
                                      </>
                                    }
                                  />
                                </ListItem>
                                {index < pestAlerts.length - 1 && <Divider />}
                              </React.Fragment>
                            ))}
                          </List>
                          
                          <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                            Alert levels are based on current temperature, humidity, and precipitation
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default WeatherPage; 